import { useEffect, useState } from 'react'
import {
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  UserIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, Avatar, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter, Pagination } from '@/components/ui'
import { servicesApi } from '@/api'
import type { ClinicCategory, ClinicQuestion, ClinicAnswer } from '@/api/services'
import toast from 'react-hot-toast'

type QuestionWithAnswers = ClinicQuestion & { answers: ClinicAnswer[] }

export default function ClinicPage() {
  const [categories, setCategories] = useState<ClinicCategory[]>([])
  const [questions, setQuestions] = useState<ClinicQuestion[]>([])
  const [selectedQuestion, setSelectedQuestion] = useState<QuestionWithAnswers | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingQuestion, setLoadingQuestion] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  // New Question Modal
  const [showNewQuestionModal, setShowNewQuestionModal] = useState(false)
  const [newQuestion, setNewQuestion] = useState({
    category_id: 0,
    title: '',
    content: '',
    is_anonymous: false,
    is_experts_only: false,
  })
  const [creating, setCreating] = useState(false)

  // Answer Modal
  const [showAnswerModal, setShowAnswerModal] = useState(false)
  const [newAnswer, setNewAnswer] = useState('')
  const [submittingAnswer, setSubmittingAnswer] = useState(false)

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    fetchQuestions()
  }, [currentPage, selectedCategory, statusFilter, searchQuery])

  const fetchCategories = async () => {
    try {
      const data = await servicesApi.getClinicCategories()
      const categoriesData = data || []
      setCategories(categoriesData)
      if (categoriesData.length > 0) {
        setNewQuestion(prev => ({ ...prev, category_id: categoriesData[0].id }))
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  const fetchQuestions = async () => {
    setLoading(true)
    try {
      const data = await servicesApi.getClinicQuestions({
        page: currentPage,
        category: selectedCategory || undefined,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
      })
      setQuestions(data.results || [])
      setTotalPages(Math.ceil((data.count || 0) / 20))
      setTotalItems(data.count || 0)
    } catch (error) {
      console.error('Error fetching questions:', error)
      setQuestions([])
    } finally {
      setLoading(false)
    }
  }

  const fetchQuestionDetail = async (id: number) => {
    setLoadingQuestion(true)
    try {
      const data = await servicesApi.getClinicQuestion(id)
      setSelectedQuestion(data)
    } catch (error) {
      console.error('Error fetching question:', error)
    } finally {
      setLoadingQuestion(false)
    }
  }

  const handleCreateQuestion = async () => {
    if (!newQuestion.title || !newQuestion.content || !newQuestion.category_id) return
    setCreating(true)
    try {
      await servicesApi.createClinicQuestion(newQuestion)
      setShowNewQuestionModal(false)
      setNewQuestion({
        category_id: categories[0]?.id || 0,
        title: '',
        content: '',
        is_anonymous: false,
        is_experts_only: false,
      })
      setCurrentPage(1)
      fetchQuestions()
    } catch (error) {
      console.error('Error creating question:', error)
    } finally {
      setCreating(false)
    }
  }

  const handleSubmitAnswer = async () => {
    if (!selectedQuestion || !newAnswer.trim()) return
    setSubmittingAnswer(true)
    try {
      await servicesApi.createClinicAnswer(selectedQuestion.id, newAnswer)
      toast.success('Risposta inviata con successo!')
      setNewAnswer('')
      setShowAnswerModal(false)
      fetchQuestionDetail(selectedQuestion.id)
    } catch (error: any) {
      console.error('Error submitting answer:', error)
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio della risposta')
    } finally {
      setSubmittingAnswer(false)
    }
  }

  const handleMarkHelpful = async (answerId: number, hasVoted: boolean | undefined) => {
    try {
      if (hasVoted) {
        await servicesApi.unmarkAnswerHelpful(answerId)
      } else {
        await servicesApi.markAnswerHelpful(answerId)
      }
      if (selectedQuestion) {
        fetchQuestionDetail(selectedQuestion.id)
      }
    } catch (error) {
      console.error('Error toggling helpful:', error)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const getStatusBadge = (status: string) => {
    // Map backend status values to frontend display
    const config: Record<string, { variant: 'info' | 'success' | 'default' | 'warning'; label: string; icon: typeof ClockIcon }> = {
      open: { variant: 'info', label: 'Aperta', icon: ClockIcon },
      pending: { variant: 'info', label: 'In attesa', icon: ClockIcon },
      assigned: { variant: 'warning', label: 'Assegnata', icon: ClockIcon },
      answered: { variant: 'success', label: 'Risposta', icon: CheckCircleIcon },
      validated: { variant: 'success', label: 'Validata', icon: CheckCircleIcon },
      published: { variant: 'success', label: 'Pubblicata', icon: CheckCircleIcon },
      closed: { variant: 'default', label: 'Chiusa', icon: XCircleIcon },
    }
    return config[status] || { variant: 'default', label: status || 'Sconosciuto', icon: ClockIcon }
  }

  if (loading && currentPage === 1 && !selectedQuestion) {
    return <InlineSpinner message="Caricamento Compliance Clinic..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Clinic</h1>
          <p className="text-gray-600">Poni domande agli esperti di compliance</p>
        </div>
        <Button onClick={() => setShowNewQuestionModal(true)}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuova Domanda
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Questions List */}
        <div className="lg:col-span-2 space-y-6">
          {/* Search and Filters */}
          <Card>
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca domande..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {/* Status Filter */}
                <div className="flex gap-2">
                  {[
                    { value: '', label: 'Tutte' },
                    { value: 'pending', label: 'In attesa' },
                    { value: 'answered', label: 'Risposte' },
                    { value: 'published', label: 'Pubblicate' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setStatusFilter(opt.value); setCurrentPage(1); }}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        statusFilter === opt.value
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Filter */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => { setSelectedCategory(''); setCurrentPage(1); }}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      !selectedCategory
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Tutte le categorie
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => { setSelectedCategory(cat.slug); setCurrentPage(1); }}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        selectedCategory === cat.slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {cat.name} ({cat.questions_count})
                    </button>
                  ))}
                </div>
              )}
            </form>
          </Card>

          {/* Questions List */}
          {questions.length > 0 ? (
            <>
              <div className="space-y-4">
                {questions.map((question) => {
                  const statusConfig = getStatusBadge(question.status)
                  const StatusIcon = statusConfig.icon

                  return (
                    <Card
                      key={question.id}
                      className={`cursor-pointer hover:shadow-md transition-shadow ${
                        selectedQuestion?.id === question.id ? 'ring-2 ring-primary-500' : ''
                      }`}
                      onClick={() => fetchQuestionDetail(question.id)}
                    >
                      <div className="flex items-start gap-4">
                        {question.is_anonymous ? (
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-gray-500" />
                          </div>
                        ) : (
                          <Avatar
                            size="md"
                            name={question.author.full_name}
                            src={question.author.avatar || undefined}
                          />
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Badge variant={statusConfig.variant} size="sm">
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                            {question.is_experts_only && (
                              <Badge variant="warning" size="sm">
                                <LockClosedIcon className="h-3 w-3 mr-1" />
                                Solo esperti
                              </Badge>
                            )}
                            <span className="text-xs text-gray-400">{question.category.name}</span>
                          </div>

                          <h3 className="font-semibold text-gray-900 mb-1">{question.title}</h3>
                          <p className="text-sm text-gray-500 line-clamp-2">{question.content}</p>

                          <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                            <span>{question.is_anonymous ? 'Anonimo' : question.author.full_name}</span>
                            <span>{formatDate(question.created_at)}</span>
                            <span className="flex items-center gap-1">
                              <EyeIcon className="h-3 w-3" />
                              {question.views_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <ChatBubbleLeftRightIcon className="h-3 w-3" />
                              {question.answers_count}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                onPageChange={setCurrentPage}
              />
            </>
          ) : (
            <Card>
              <EmptyState
                icon={<QuestionMarkCircleIcon className="h-12 w-12" />}
                title="Nessuna domanda"
                description={searchQuery || statusFilter || selectedCategory
                  ? "Prova a modificare i filtri di ricerca"
                  : "Sii il primo a porre una domanda agli esperti"
                }
                action={{
                  label: 'Nuova Domanda',
                  onClick: () => setShowNewQuestionModal(true),
                }}
              />
            </Card>
          )}
        </div>

        {/* Right Column - Question Detail */}
        <div className="space-y-6">
          {loadingQuestion ? (
            <Card>
              <InlineSpinner message="Caricamento..." />
            </Card>
          ) : selectedQuestion ? (
            <>
              {/* Question Detail */}
              <Card>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusBadge(selectedQuestion.status).variant}>
                      {getStatusBadge(selectedQuestion.status).label}
                    </Badge>
                    <span className="text-sm text-gray-500">{selectedQuestion.category.name}</span>
                  </div>

                  <h2 className="text-lg font-semibold text-gray-900">{selectedQuestion.title}</h2>

                  <div className="flex items-center gap-3">
                    {selectedQuestion.is_anonymous ? (
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <UserIcon className="h-4 w-4 text-gray-500" />
                      </div>
                    ) : (
                      <Avatar
                        size="sm"
                        name={selectedQuestion.author.full_name}
                        src={selectedQuestion.author.avatar || undefined}
                      />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedQuestion.is_anonymous ? 'Anonimo' : selectedQuestion.author.full_name}
                      </p>
                      <p className="text-xs text-gray-500">{formatDate(selectedQuestion.created_at)}</p>
                    </div>
                  </div>

                  <div className="text-gray-600 text-sm whitespace-pre-wrap">
                    {selectedQuestion.content}
                  </div>

                  {['pending', 'assigned', 'open'].includes(selectedQuestion.status) && (
                    <Button className="w-full" onClick={() => setShowAnswerModal(true)}>
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                      Rispondi
                    </Button>
                  )}
                </div>
              </Card>

              {/* Answers */}
              {selectedQuestion.answers.length > 0 && (
                <Card>
                  <CardHeader title="Risposte" subtitle={`${selectedQuestion.answers.length} risposte`} />
                  <div className="space-y-4 divide-y divide-gray-100">
                    {selectedQuestion.answers.map((answer) => (
                      <div key={answer.id} className="pt-4 first:pt-0">
                        <div className="flex items-start gap-3">
                          <Avatar
                            size="sm"
                            name={answer.expert.full_name}
                            src={answer.expert.avatar || undefined}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">{answer.expert.full_name}</span>
                              {answer.is_official && (
                                <Badge variant="success" size="sm">Ufficiale</Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mb-2">{answer.expert.role}</p>
                            <div className="text-sm text-gray-600 whitespace-pre-wrap">
                              {answer.content}
                            </div>
                            <div className="flex items-center gap-4 mt-3">
                              <button
                                onClick={() => handleMarkHelpful(answer.id, answer.has_voted_helpful)}
                                className={`flex items-center gap-1 text-sm ${
                                  answer.has_voted_helpful
                                    ? 'text-primary-600 font-medium'
                                    : 'text-gray-500 hover:text-primary-600'
                                }`}
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                                Utile ({answer.helpful_count})
                              </button>
                              <span className="text-xs text-gray-400">
                                {formatDate(answer.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <EmptyState
                icon={<QuestionMarkCircleIcon className="h-12 w-12" />}
                title="Seleziona una domanda"
                description="Clicca su una domanda per vedere i dettagli e le risposte"
              />
            </Card>
          )}
        </div>
      </div>

      {/* New Question Modal */}
      <Modal isOpen={showNewQuestionModal} onClose={() => setShowNewQuestionModal(false)} title="Nuova Domanda">
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
              <select
                value={newQuestion.category_id}
                onChange={(e) => setNewQuestion({ ...newQuestion, category_id: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
              <input
                type="text"
                value={newQuestion.title}
                onChange={(e) => setNewQuestion({ ...newQuestion, title: e.target.value })}
                placeholder="Riassumi la tua domanda..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dettagli *</label>
              <textarea
                value={newQuestion.content}
                onChange={(e) => setNewQuestion({ ...newQuestion, content: e.target.value })}
                rows={5}
                placeholder="Descrivi la tua domanda in dettaglio..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_anonymous"
                  checked={newQuestion.is_anonymous}
                  onChange={(e) => setNewQuestion({ ...newQuestion, is_anonymous: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_anonymous" className="text-sm text-gray-700">
                  Posta in modo anonimo
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_experts_only"
                  checked={newQuestion.is_experts_only}
                  onChange={(e) => setNewQuestion({ ...newQuestion, is_experts_only: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_experts_only" className="text-sm text-gray-700">
                  Visibile solo agli esperti (non agli altri membri)
                </label>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowNewQuestionModal(false)}>Annulla</Button>
          <Button
            onClick={handleCreateQuestion}
            disabled={!newQuestion.title || !newQuestion.content || creating}
          >
            {creating ? 'Invio...' : 'Invia Domanda'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Answer Modal */}
      <Modal isOpen={showAnswerModal} onClose={() => setShowAnswerModal(false)} title="Rispondi alla domanda">
        <ModalBody>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">La tua risposta</label>
            <textarea
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              rows={6}
              placeholder="Scrivi la tua risposta..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowAnswerModal(false)}>Annulla</Button>
          <Button
            onClick={handleSubmitAnswer}
            disabled={!newAnswer.trim() || submittingAnswer}
          >
            {submittingAnswer ? 'Invio...' : 'Invia Risposta'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
